import { Format, IntegerFormatter, PercentageFormatter } from '@zervant/reactcomponents'
import { EInvoiceCharacterLimitInfo } from 'client/components/common/EInvoiceCharacterLimitInfo'
import { Segment } from 'client/Global/Segment'
import { SegmentEvents } from 'client/Global/SegmentEvents'
import { l, tsxl } from 'client/Other/Translation'
import { setBaseClass } from 'client/utils'
import _ from 'lodash'
import moment from 'moment'
import * as React from 'react'

import { FieldInterface } from '../../../Interfaces/Draft/FieldInterface'
import { FieldsInterface } from '../../../Interfaces/Draft/FieldsInterface'
import { InvoiceHeaderInterface } from '../../../Interfaces/InvoiceHeaderInterface'
import { LabelKeys } from '../../../Interfaces/LabelsInterface'
import { SchemaInterface } from '../../../Interfaces/Schema/SchemaInterface'
import { ModelInterface } from '../../../Interfaces/Settings/ModelInterface'
import { SettingsInterface } from '../../../Interfaces/Settings/SettingsInterface'
import { calculateDaysFromStartDate, transformDateFormatForMoment } from '../../../Other/functions/dateFunctions'
import { _isFieldVisible } from '../../../Other/functions/modelFunctions'
import { correctReferenceNumber } from '../../../Other/functions/referenceNumberFunctions'
import { convertPctFromLocale } from '../../../Other/functions/vatFunctions'
import { FieldCreator } from '../../common/FieldCreator'
import Input from '../../common/Input/Input'
import { TestableInterface } from '../../common/interfaces/TestableInterface'
import { Layout, LayoutColumn } from '../../common/Layout/Layout'
import { ReferenceNumberField } from './ReferenceNumberField'

export interface InvoiceHeaderPropsInterface extends TestableInterface {
  fields: FieldsInterface
  settings: SettingsInterface
  locale: string
  model: ModelInterface
  schema: SchemaInterface
  invalidFields: string[]
  invoiceNumberEditable: boolean
  editableInvoiceNumberInput?: boolean
  country: string
  eInvoiceCharacterLimit: boolean
}

export interface InvoiceHeaderActionsInterface {
  onChange: (field: FieldInterface) => void
  onDocumentNumberEdit: () => void
  openPaymentTermsModal: (afterF: (value) => void) => void
  openLatePaymentInterestModal: (afterF: (value) => void) => void
}

export interface InvoiceHeaderStateInterface extends InvoiceHeaderInterface {
  focused?: boolean
}

export class InvoiceHeader extends React.Component<
  InvoiceHeaderPropsInterface & InvoiceHeaderActionsInterface,
  InvoiceHeaderStateInterface
> {
  private int: IntegerFormatter

  constructor(props) {
    super(props)

    const format = Format.fromLocale(props.locale)
    format.numbers.isGrouped = false
    this.int = new IntegerFormatter(format)

    this.state = {
      focused: false,
    }

    this.onFocusChange = this.onFocusChange.bind(this)
  }

  private onFocusChange({ focused }) {
    this.setState({ focused })
  }

  private getMap(fieldName: string) {
    const map = {}
    this.props.settings?.managedFields[fieldName]?.map((name) => {
      // special handling for interest field
      switch (fieldName) {
        case 'PAYMENT_INTEREST':
          map[parseFloat(name)] = convertPctFromLocale(name, this.props.locale)
          break
        case 'PAYMENT_TERMS':
          map[parseInt(name, 10)] = name + ' ' + l('days')
          break
        default:
          map[name] = name
      }
    })
    return map
  }

  private getReferenceNumber() {
    let ref = ''
    if (this.props.fields.referenceNumber && /^[0-9]{1,20}$/.test(this.props.fields.referenceNumber.value)) {
      ref = this.props.fields.referenceNumber.value
    }
    return ref
  }

  private correctReferenceNumber(value) {
    return ['FI', 'AX'].includes(this.props.country) ? correctReferenceNumber(value) : value
  }

  private changeReferenceNumber(value) {
    value = value.replace(/[^0-9]/g, '')
    if (!value || /^[0-9]{1,20}$/.test(value)) {
      this.props.onChange({ ...this.props.fields.referenceNumber, value: this.correctReferenceNumber(value) })
    }
  }

  private isDueDateVisible(): boolean {
    return _isFieldVisible('dueDate', this.props.model, this.props.schema)
  }

  private isDueDateEditable(): boolean {
    return !_isFieldVisible('paymentTime', this.props.model, this.props.schema)
  }

  private sendInvoiceHeaderChangeEventToSegment(name: string) {
    if (name === 'buyerReference') {
      Segment.track(SegmentEvents.invoice.invoiceFieldsFilled, { buyers_reference_filled: true })
    }
    if (name === 'recipientReference') {
      Segment.track(SegmentEvents.invoice.invoiceFieldsFilled, { buyers_purchase_order_number_filled: true })
    }
  }

  public render(): JSX.Element {
    const recurringEnabled =
      this.props.fields.recurringProfileEnabled && this.props.fields.recurringProfileEnabled.value
    const type = recurringEnabled ? 'recurringProfile' : this.props.model.type
    const labels = LabelKeys[type]
    const now = moment()
    const defaultPaymentTerm = 30
    const defaultDuedate = calculateDaysFromStartDate(now, defaultPaymentTerm)
    // Make sure issue date and due date always have values
    const issueDate = _.get(this.props.fields, 'issueDate.value', false) || now.toISOString()
    const issueDateReadOnly =
      this.props.fields.recurringProfileEnabled && this.props.fields.recurringProfileEnabled.value
    const dueDate = _.get(this.props.fields, 'dueDate.value', false) || defaultDuedate.toISOString()
    let isCreditNote = _.get(this.props.fields, 'creditnoteNumber.value', false)
    const dateFormat = transformDateFormatForMoment(this.props.settings?.values?.regional?.longDate)
    if (isCreditNote !== false) {
      isCreditNote = true
    }
    let dueDateField
    if (this.isDueDateVisible()) {
      dueDateField = (
        <FieldCreator
          name="dueDate"
          model={this.props.model}
          schema={this.props.schema}
          translator={tsxl}
          props={{
            automation: this.props.automation + '-dueDate',
            todayButton: l('Today'),
            value: dueDate,
            format: dateFormat,
            locale: this.props.locale,
            invalid: this.props.invalidFields && this.props.invalidFields.indexOf('dueDate') >= 0,
            disabled: !this.isDueDateEditable(),
            onChange: (value) => {
              this.props.onChange({ ...this.props.fields.dueDate, value })
            },
          }}
        />
      )
    }
    const percentageFormatter = new PercentageFormatter(Format.fromLocale(this.props.locale), true)
    const referenceFieldsOrderField = this.props.model.fields.find(({ name }) => name === 'referenceFieldsOrder')
    const hasRefOrder = Boolean(referenceFieldsOrderField)
    const referenceFieldsOrdered = hasRefOrder
      ? (referenceFieldsOrderField.default as string).split(',')
      : ['recipientReference']
    return (
      <div data-automation={this.props.automation} className="InvoiceHeader">
        <Layout>
          <LayoutColumn>
            <div className="tight">
              {this.props.fields.creditnoteNumber && (
                <Input
                  borderless={true}
                  editable={false}
                  automation={this.props.automation + '-creditnoteNumber'}
                  value={this.props.fields.creditnoteNumber.value}
                  align={'left'}
                  label={l(labels.entityNumber)}
                />
              )}
              <div>
                <Input
                  borderless={!this.props.editableInvoiceNumberInput}
                  editable={!!this.props.editableInvoiceNumberInput}
                  automation={this.props.automation + '-invoiceNumber'}
                  value={this.props.fields.invoiceNumber ? this.props.fields.invoiceNumber.value : '0'}
                  align={'left'}
                  label={l(labels.entityNumber)}
                  invalid={this.props.invalidFields && this.props.invalidFields.includes('documentNumber')}
                  onChange={(val) => {
                    let estimateNumber = val || ''
                    estimateNumber = estimateNumber.replace(/[^0-9]/g, '').trim()
                    this.props.onChange({ ...this.props.fields.invoiceNumber, value: estimateNumber })
                  }}
                />
                {this.props.invoiceNumberEditable && (
                  <span
                    className="chooseNumber"
                    onClick={() => {
                      this.props.onDocumentNumberEdit()
                    }}
                  >
                    {l('choose')}
                  </span>
                )}
              </div>
              <FieldCreator
                name="issueDate"
                model={this.props.model}
                schema={this.props.schema}
                translator={tsxl}
                props={{
                  automation: this.props.automation + '-date',
                  todayButton: l('Today'),
                  value: issueDate,
                  invalid: this.props.invalidFields && this.props.invalidFields.indexOf('issueDate') >= 0,
                  locale: this.props.locale,
                  format: dateFormat,
                  onChange: (date) => {
                    this.props.onChange({ ...this.props.fields.issueDate, value: date })
                  },
                  disabled: isCreditNote || issueDateReadOnly,
                }}
              />
              <FieldCreator
                name="paymentTime"
                model={this.props.model}
                schema={this.props.schema}
                translator={tsxl}
                props={{
                  value: this.props.fields.paymentTime ? this.props.fields.paymentTime.value : '',
                  invalid: this.props.invalidFields && this.props.invalidFields.indexOf('paymentTime') >= 0,
                  searchable: false,
                  searchPlaceholder: l('search.Default'),
                  map: this.getMap('PAYMENT_TERMS'),
                  automation: this.props.automation + '-paymentTerms',
                  onChange: (value) => {
                    this.props.onChange({ ...this.props.fields.paymentTime, value: parseInt(value, 10) })
                  },
                  disabled: isCreditNote,
                  addNewLabel: l('input.select.EditValues'),
                  onAddNew: () => {
                    this.props.openPaymentTermsModal((newVal) => {
                      this.props.onChange({ ...this.props.fields.paymentTime, value: parseInt(newVal, 10) })
                    })
                  },
                }}
              />
              {dueDateField}
              {!this.props.fields.creditnoteNumber && (
                <FieldCreator
                  name="interest"
                  model={this.props.model}
                  schema={this.props.schema}
                  translator={tsxl}
                  props={{
                    value: this.props.fields.interest ? this.props.fields.interest.value : '',
                    invalid: this.props.invalidFields && this.props.invalidFields.indexOf('interest') >= 0,
                    searchable: false,
                    searchPlaceholder: l('search.Default'),
                    map: this.getMap('PAYMENT_INTEREST'),
                    automation: this.props.automation + '-interest',
                    onChange: (value) => {
                      this.props.onChange({
                        name: 'interest',
                        visible: true,
                        value: parseFloat(value),
                      })
                    },
                    addNewLabel: l('input.select.EditValues'),
                    onAddNew: () => {
                      this.props.openLatePaymentInterestModal((newVal) => {
                        this.props.onChange({
                          name: 'interest',
                          visible: true,
                          value: parseFloat(newVal),
                        })
                      })
                    },
                  }}
                />
              )}
              {this.props.fields.deduction && this.props.fields.deduction.visible && (
                <FieldCreator
                  name="deduction"
                  model={this.props.model}
                  schema={this.props.schema}
                  translator={tsxl}
                  props={{
                    align: 'left',
                    formatter: percentageFormatter,
                    value: this.props.fields.deduction ? this.props.fields.deduction.value : '',
                    invalid: this.props.invalidFields && this.props.invalidFields.indexOf('deduction') >= 0,
                    automation: this.props.automation + '-deduction',
                    onBlur: (value) => {
                      value = Math.abs(value)
                      if (value > 100) {
                        value = this.props.fields.deduction.value
                      }
                      this.props.onChange({ ...this.props.fields.deduction, value })
                    },
                  }}
                />
              )}
              {referenceFieldsOrdered.map((rf) => {
                const cls = setBaseClass(rf)
                return (
                  this.props.fields[rf] &&
                  this.props.fields[rf].visible &&
                  (rf !== 'referenceNumber' || !this.props.fields.paymentReferenceType ? (
                    <div>
                      <Input
                        key={rf}
                        {...{
                          className: ['referenceFieldInput'],
                          helpTooltipText: l(`invoiceEditor.${rf}.helperText`),
                          value: this.props.fields[rf].value || '',
                          invalid: this.props.invalidFields && this.props.invalidFields.indexOf(rf) >= 0,
                          automation: this.props.automation + `-${rf}`,
                          label: l(`invoiceEditor.${rf}`),
                          placeHolder: l(`invoiceEditor.${rf}.placeholder`),
                          onBlur: (value) => {
                            this.sendInvoiceHeaderChangeEventToSegment(rf)
                            this.props.onChange({ ...this.props.fields[rf], value })
                          },
                          editable: !isCreditNote,
                          borderless: isCreditNote,
                        }}
                      />
                      {this.props.invalidFields && this.props.invalidFields.indexOf(rf) >= 0 && (
                        <div className={cls('__input-invalid-msg')}>
                          <div className={cls('__input-invalid-msg-inner')}>
                            {l(`invoiceEditor.${rf}.invalid.missing`)}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <ReferenceNumberField
                      fields={this.props.fields}
                      onFieldChange={this.props.onChange}
                      editable={!isCreditNote}
                      borderless={isCreditNote}
                    />
                  ))
                )
              })}
            </div>
          </LayoutColumn>
          <LayoutColumn>
            <div className="tight">
              <FieldCreator
                name="senderReference"
                model={this.props.model}
                schema={this.props.schema}
                translator={tsxl}
                props={{
                  automation: this.props.automation + '-senderReference',
                  labelPosition: 'top',
                  value: this.props.fields.senderReference ? this.props.fields.senderReference.value : '',
                  invalid: this.props.invalidFields && this.props.invalidFields.indexOf('senderReference') >= 0,
                  onBlur: (value) => {
                    this.props.onChange({ ...this.props.fields.senderReference, value })
                  },
                }}
              ></FieldCreator>
              <FieldCreator
                name="description"
                model={this.props.model}
                schema={this.props.schema}
                translator={tsxl}
                props={{
                  automation: this.props.automation + '-message',
                  labelPosition: 'top',
                  value: this.props.fields.description ? this.props.fields.description.value : '',
                  invalid: this.props.eInvoiceCharacterLimit && this.props.fields?.description?.value?.length > 1024,
                  onBlur: (value) => {
                    const description = this.props.fields.description || {
                      name: 'description',
                      value: '',
                      visible: true,
                    }
                    this.props.onChange({ ...description, value })
                  },
                  maxLength: 3000,
                  showCounter: true,
                  resizable: true,
                }}
              />
              {this.props.fields?.description?.value?.length > 1024 && (
                <EInvoiceCharacterLimitInfo
                  invalid={this.props.eInvoiceCharacterLimit && this.props.fields?.description?.value?.length > 1024}
                />
              )}
            </div>
          </LayoutColumn>
        </Layout>
      </div>
    )
  }
}
